import { useState } from "react";
import { format } from "date-fns";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getClientFileUrl,
  useClientFiles,
  useDeleteClientFile,
  useUploadClientFile,
} from "@/features/clients/api";
import { useClientId } from "@/features/clients/detail/use-client-id";
import { HubToolbar } from "@/features/clients/detail/hub-toolbar";
import type { ClientFile } from "@/features/clients/types";
import { cn } from "@/lib/utils";

export function ClientFilesPage() {
  const clientId = useClientId();
  const { data: files = [], isLoading } = useClientFiles(clientId);
  const upload = useUploadClientFile(clientId);
  const remove = useDeleteClientFile(clientId);
  const [dragging, setDragging] = useState(false);
  const [deleting, setDeleting] = useState<ClientFile | null>(null);

  function handleFiles(list: FileList | null) {
    if (!list?.length) return;
    Array.from(list).forEach((file) => void upload.mutateAsync(file));
  }

  return (
    <div>
      <HubToolbar
        title="Files"
        description="GST certificate, PO copies, drawings, signed contracts."
        action={
          <Button asChild size="sm" disabled={upload.isPending}>
            <label className="cursor-pointer">
              <Upload />
              {upload.isPending ? "Uploading…" : "Upload"}
              <input
                type="file"
                multiple
                className="sr-only"
                onChange={(e) => {
                  handleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
          </Button>
        }
      />

      <div className="p-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          "rounded-lg border border-dashed border-border transition-colors",
          dragging && "border-primary bg-primary/5",
        )}
      >
        {isLoading ? (
          <p className="py-8 text-center text-xs text-muted-foreground">Loading…</p>
        ) : files.length === 0 ? (
          <EmptyState
            title="No files yet"
            description="Drop files here or use the upload button."
            className="border-0 bg-transparent"
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>File</TableHead>
                <TableHead className="w-24">Size</TableHead>
                <TableHead className="w-32">Uploaded</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((file) => (
                <TableRow key={file.id} className="group">
                  <TableCell>
                    <span className="flex min-w-0 items-center gap-2">
                      <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{file.name}</span>
                    </span>
                  </TableCell>
                  <TableCell className="tabular text-muted-foreground">
                    {formatSize(file.size_bytes)}
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular">
                    {format(new Date(file.created_at), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Download"
                        onClick={async () => {
                          const url = await getClientFileUrl(file.storage_path);
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                      >
                        <Download />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Delete file"
                        className="hover:text-destructive"
                        onClick={() => setDeleting(file)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      </div>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete file?"
        description={deleting ? `${deleting.name} will be removed from storage.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting);
          setDeleting(null);
        }}
      />
    </div>
  );
}

function formatSize(bytes: number | null) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
